import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const REVIEW_DESIGN_LABELS = {
  'Meta-analysis': 'Systematic Review with Meta-Analysis',
  'Systematic review': 'Systematic Review (no Meta-Analysis)',
  'Scoping review': 'Scoping Review',
  'Critical appraisal review': 'Critical Appraisal Review',
  'Narrative review': 'Narrative Review',
};

export const EvidenceSynthesisPanel = ({ articleId }) => {
  const [ess, setEss] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${API_URL}/api/articles/${articleId}/ess-breakdown`);
        if (res.ok) setEss(await res.json());
      } catch (e) {
        console.error('Error loading ESS:', e);
      }
      setLoading(false);
    };
    load();
  }, [articleId]);

  if (loading) return <Card className="mb-8 animate-pulse"><CardContent className="py-8"><p className="text-slate-500 text-center">Loading Evidence Synthesis Score...</p></CardContent></Card>;
  if (!ess) return null;

  const reviewLabel = REVIEW_DESIGN_LABELS[ess.review_type] || ess.review_type;

  return (
    <Card className="mb-8 border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50" data-testid="ess-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-teal-900 flex items-center gap-2">
            Evidence Synthesis Score (ESS)
          </CardTitle>
          <Badge className="bg-teal-600 text-white text-lg px-3 py-1" data-testid="ess-total-badge">
            {ess.total_ess} / 35
          </Badge>
        </div>
        <p className="text-sm text-teal-700 mt-1">
          Scored as: <span className="font-semibold">{reviewLabel}</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* 1. Review Design Score */}
        <div data-testid="ess-review-design">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-slate-800">Review Design Score</span>
            <span className="font-semibold text-teal-700">{ess.review_design_score} / 15</span>
          </div>
          <Progress value={(ess.review_design_score / 15) * 100} className="h-2.5 bg-teal-100" />
          <p className="text-xs text-slate-500 mt-1">Deterministic — based on review type: {reviewLabel}</p>
        </div>

        {/* 2. Transparency Score */}
        <div data-testid="ess-transparency">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-slate-800">Transparency Score</span>
            <span className="font-semibold text-teal-700">{ess.transparency_score} / 10</span>
          </div>
          <Progress value={(ess.transparency_score / 10) * 100} className="h-2.5 bg-teal-100" />
          {ess.transparency_breakdown && Object.keys(ess.transparency_breakdown).length > 0 && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {Object.entries(ess.transparency_breakdown).map(([key, val]) => (
                <div key={key} className="flex justify-between text-xs text-slate-600 py-0.5">
                  <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="font-medium">{val > 0 ? `+${val}` : '0'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 3. Synthesis Structure Score */}
        <div data-testid="ess-synthesis">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-slate-800">Synthesis Structure Score</span>
            <span className="font-semibold text-teal-700">{ess.synthesis_structure_score} / 5</span>
          </div>
          <Progress value={(ess.synthesis_structure_score / 5) * 100} className="h-2.5 bg-teal-100" />
        </div>

        {/* 4. Clinical Relevance Score */}
        <div data-testid="ess-clinical-relevance">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-slate-800">Clinical Relevance Score</span>
            <span className="font-semibold text-teal-700">{ess.clinical_relevance_score} / 5</span>
          </div>
          <Progress value={(ess.clinical_relevance_score / 5) * 100} className="h-2.5 bg-teal-100" />
        </div>

        {/* AI Justifications */}
        {ess.component_justifications && (
          <div className="border-t border-teal-200 pt-4 mt-2 space-y-2">
            <p className="text-sm font-semibold text-teal-900">AI Assessment Notes</p>
            {Object.entries(ess.component_justifications).map(([key, text]) => (
              <div key={key} className="text-sm text-slate-700">
                <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                <span>{text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Overall explanation */}
        {ess.evaluation_explanation && (
          <div className="bg-white/60 rounded-lg p-3 mt-2 border border-teal-100">
            <p className="text-sm text-teal-900 font-medium">{ess.evaluation_explanation}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
